import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box, Flex } from '@chakra-ui/react';
import { Avatar, Text, DotsLoader } from '@kula-ai/components';
import type { MentionSuggestion } from '../../api/mentions';

export interface MentionSuggestionsPopupProps {
  open: boolean;
  loading: boolean;
  query: string;
  suggestions: MentionSuggestion[];
  highlightedIndex: number;
  // Anchored to the viewport bottom (distance from bottom edge) so the
  // popup grows upward — caret stays visible, list opens above it.
  position: { bottom: number; left: number } | null;
  onSelect: (_suggestion: MentionSuggestion) => void;
  onHover: (_index: number) => void;
}

const POPUP_WIDTH = 430;
const POPUP_MAX_HEIGHT = 300;
// A global rule (styles/index.css) pins every Radix popper — including this
// popup's own truncate tooltips — to z-index 1500. Sit just under that (but
// above the widget/thread's "overlay" layer, 1400) so a truncated name/role
// tooltip renders on top of the popup instead of behind it.
const POPUP_Z = 1450;

// People carry an avatar; jobs/flows render as a plain text row.
const PEOPLE_ENTITIES = new Set(['contact', 'user']);

const titleCase = (entity: string): string => {
  const trimmed = entity.trim();
  if (trimmed.length === 0) return '';
  return trimmed[0].toUpperCase() + trimmed.slice(1);
};

// Section header shown above each entity's rows. Falls back to the pluralised
// entity name for anything the backend adds later.
const GROUP_LABELS: Record<string, string> = {
  job: 'Jobs',
  user: 'Users',
  contact: 'Candidates',
  flow: 'Flows',
};
const groupLabel = (entity: string): string =>
  GROUP_LABELS[entity] ?? `${titleCase(entity)}s`;

export const MentionSuggestionsPopup = ({
  open,
  loading,
  query,
  suggestions,
  highlightedIndex,
  position,
  onSelect,
  onHover,
}: MentionSuggestionsPopupProps) => {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || highlightedIndex < 0) return;
    const active = listRef.current?.querySelector<HTMLElement>(
      `[data-mention-index="${highlightedIndex}"]`
    );
    active?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, open]);

  if (!open || !position) return null;

  // Keep popup inside the viewport horizontally; if the caret is too close
  // to the right edge, shift left so the popup doesn't get clipped.
  const left = Math.min(
    position.left,
    Math.max(8, window.innerWidth - POPUP_WIDTH - 8)
  );

  const content = (
    <Box
      position="fixed"
      bottom={`${position.bottom}px`}
      left={`${left}px`}
      width={`${POPUP_WIDTH}px`}
      maxHeight={`${POPUP_MAX_HEIGHT}px`}
      bg="base-white"
      border="1px solid"
      borderColor="border-color"
      borderRadius="10px"
      boxShadow="0px 8px 24px rgba(38, 34, 68, 0.10)"
      overflow="hidden"
      zIndex={POPUP_Z}
      // Mouse-down on the popup must NOT blur the editor — otherwise we
      // lose the saved caret range before we can insert the mention.
      onMouseDown={(e) => e.preventDefault()}
      data-test-id="mention-suggestions-popup"
    >
      {!query.trim() ? (
        <Box px="12px" py="10px">
          <Text variant="p" className="text-secondary-500">
            Directly tag jobs, employees, candidates and more...
          </Text>
        </Box>
      ) : loading && suggestions.length === 0 ? (
        <Flex align="center" justify="center" py="14px">
          <DotsLoader />
        </Flex>
      ) : suggestions.length === 0 ? (
        <Box px="12px" py="10px">
          <Text variant="p" className="text-secondary-500">
            No matches for &ldquo;{query}&rdquo;
          </Text>
        </Box>
      ) : (
        <Box
          ref={listRef}
          maxHeight={`${POPUP_MAX_HEIGHT}px`}
          overflowY="auto"
          role="listbox"
        >
          {suggestions.map((s, idx) => {
            const active = idx === highlightedIndex;
            const isPerson = PEOPLE_ENTITIES.has(s.entity);
            // Grouped by entity: the flat list is entity-contiguous, so a new
            // header starts wherever the entity changes.
            const startsGroup =
              idx === 0 || suggestions[idx - 1].entity !== s.entity;
            return (
              <Box key={`${s.entity}-${s.id}`}>
                {startsGroup && (
                  <Box
                    bg="secondary-25"
                    px="16px"
                    py="8px"
                    borderBottom="1px solid"
                    borderColor="secondary-100"
                  >
                    <Text
                      variant="overhead"
                      as="div"
                      className="text-secondary-900"
                    >
                      {groupLabel(s.entity)}
                    </Text>
                  </Box>
                )}
                <Flex
                  align="center"
                  gap="10px"
                  px="16px"
                  py="11px"
                  cursor="pointer"
                  bg={active ? 'secondary-50' : 'transparent'}
                  _hover={{ bg: 'secondary-50' }}
                  borderBottom="1px solid"
                  borderColor="secondary-50"
                  onMouseEnter={() => onHover(idx)}
                  onClick={() => onSelect(s)}
                  role="option"
                  aria-selected={active}
                  data-mention-index={idx}
                  data-test-id={`mention-suggestion-${s.id}`}
                >
                  {isPerson && (
                    <Avatar
                      src={s.avatarUrl}
                      name={s.name}
                      shape="circle"
                      size={22}
                    />
                  )}
                  <Text
                    variant="h4"
                    as="span"
                    truncate
                    className="flex-1 text-secondary-700"
                  >
                    {s.name}
                  </Text>
                  {isPerson && s.subtitle && (
                    <Text
                      variant="p"
                      as="span"
                      truncate
                      className="text-secondary-500 shrink-0 max-w-[45%] text-right"
                    >
                      {s.subtitle}
                    </Text>
                  )}
                </Flex>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );

  return createPortal(content, document.body);
};
